'use client';

/**
 * DealSheetThumbnail — rotated term-sheet card mockup used as a hero
 * element on the white-labeled marketing site.
 *
 * Reference: `pages/coastal-v4.jsx` Hero (lines ~163-253). The visual is
 * intentionally graphical — a "this is what you get" mock that lives in
 * the design system because the wordmark is brand-config-driven.
 *
 * Layout from top to bottom:
 *  1. Paper edge with hairline rule
 *  2. Header — wordmark (left) + `TERM SHEET / CRF·NNNN·NNN` (right)
 *  3. Borrower line (hairline divider above)
 *  4. Subject property block
 *  5. 6-cell terms grid (loan / type / rate / points / term / monthly)
 *  6. Sand-toned LTC / LTARV / FICO strip
 *  7. Italic signature line + mint stamp
 *  8. Optional rotated "SENT IN 24 HRS" corner tag
 */

import { Box } from '@mui/material';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef, type ReactNode } from 'react';
import { useI18n } from '../../hooks';
import { baseColors } from '../../theme/palette';
import { StackedWordmark } from '../StackedWordmark';

export interface DealSheetBrand {
  /** Top wordmark line (e.g. `COASTAL`). */
  wordmark: string;
  /** Bottom wordmark line (e.g. `FUNDING`). */
  wordmarkSub?: string;
  /** Brand accent color (used for the approval stamp). */
  accent: string;
  /** Optional ink (text) color. Defaults to deep navy. */
  ink?: string;
}

export interface DealSheetTerms {
  loan: string;
  rate: string;
  points: string;
  term: string;
  monthly: string;
  /** Loan type label (e.g. `Bridge / F&F`). */
  loanType?: string;
}

export interface DealSheetSummary {
  ltc: string;
  ltarv: string;
  fico: string;
}

export type DealSheetStamp = 'APPROVED' | 'PENDING' | null;

export interface DealSheetThumbnailProps {
  /** Brand config — wordmark, accent. */
  brand: DealSheetBrand;
  /** Deal identifier label rendered at top-right. */
  dealNumber: string;
  /** Borrower entity name + optional EIN line. */
  borrower: {
    name: string;
    meta?: ReactNode;
  };
  /** Subject property line(s). */
  property: {
    address: string;
    meta?: ReactNode;
  };
  /** Loan term values. */
  terms: DealSheetTerms;
  /** Sizing strip values. */
  summary: DealSheetSummary;
  /** Italic signature text rendered on the signature line. */
  signature: string;
  /** Stamp text — `APPROVED` shows mint stamp, `PENDING` shows amber, `null` hides. */
  stamp?: DealSheetStamp;
  /**
   * Optional override for the stamp label text. When set, replaces the
   * default i18n label (`website.dealSheet.stampApproved` / `…stampPending`).
   * Use this for surface-specific copy like the home hero's "APPROVED · 7 DAYS"
   * pull-quote stamp without changing the generic component default.
   */
  stampText?: string;
  /** Optional rotated corner tag (e.g. "SENT IN 24 HRS"). */
  corner?: string;
  /** Card rotation in degrees. Defaults to `-1.5` (matches Coastal v4 hero). */
  rotation?: number;
  sx?: SxProps<Theme>;
}

/**
 * Rotated term-sheet hero mock.
 */
export const DealSheetThumbnail = forwardRef<HTMLDivElement, DealSheetThumbnailProps>(function DealSheetThumbnail(
  {
    brand,
    dealNumber,
    borrower,
    property,
    terms,
    summary,
    signature,
    stamp = 'APPROVED',
    stampText,
    corner,
    rotation = -1.5,
    sx,
  },
  ref,
) {
  const theme = useTheme();
  const { t } = useI18n();
  // Use light-mode-style canvas regardless of portal theme — this is a
  // paper mock that should always read as a printed document.
  const paper = baseColors.neutral[0];
  const ink = brand.ink ?? baseColors.neutral[900];
  const border = baseColors.neutral[200];
  const textDim = baseColors.neutral[500];
  const textMute = baseColors.neutral[400];
  const bgWarm = baseColors.neutral[100];

  const stampColor = stamp === 'PENDING' ? theme.palette.warning.main : brand.accent;
  const stampLabel =
    stampText ??
    (stamp === 'APPROVED'
      ? t('website.dealSheet.stampApproved')
      : stamp === 'PENDING'
        ? t('website.dealSheet.stampPending')
        : '');

  // 6 terms cells — value font sizes (large on loan amount, smaller on the rest)
  const cells: Array<[label: string, value: string, big?: boolean]> = [
    [t('website.dealSheet.loanAmount'), terms.loan, true],
    [t('website.dealSheet.loanType'), terms.loanType ?? ''],
    [t('website.dealSheet.rate'), terms.rate],
    [t('website.dealSheet.points'), terms.points],
    [t('website.dealSheet.term'), terms.term],
    [t('website.dealSheet.monthlyInt'), terms.monthly],
  ];

  return (
    <Box
      ref={ref}
      sx={[
        {
          position: 'relative',
          // The `perspective` value matches the coastal-v4 hero so multiple
          // tilted assets sit in the same depth.
          perspective: '1400px',
          display: 'inline-block',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Optional corner tag */}
      {corner && (
        <Box
          sx={{
            position: 'absolute',
            top: -14,
            right: -10,
            px: 1.375,
            py: 0.75,
            backgroundColor: ink,
            color: paper,
            fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
            fontSize: '0.5625rem',
            letterSpacing: '0.12em',
            fontWeight: 700,
            zIndex: 3,
            transform: 'rotate(2deg)',
            borderRadius: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {corner}
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: paper,
          border: `1px solid ${border}`,
          boxShadow: `0 24px 64px -24px ${alpha(ink, 0.25)}, 0 8px 16px -8px ${alpha(ink, 0.1)}`,
          borderRadius: 0.5,
          position: 'relative',
          overflow: 'hidden',
          transform: `rotate(${rotation}deg)`,
          width: 360,
          maxWidth: '100%',
        }}
      >
        {/* Paper top edge with rule */}
        <Box
          sx={{
            height: 4,
            background: `linear-gradient(90deg, ${ink} 0%, ${ink} 80px, ${border} 80px)`,
          }}
        />

        <Box sx={{ px: 3, pt: 2.5, pb: 2.25 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.25 }}>
            <StackedWordmark top={brand.wordmark} bottom={brand.wordmarkSub ?? ''} size="sm" color={ink} />
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                component="div"
                sx={{
                  fontSize: '0.53125rem',
                  color: textMute,
                  fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                {t('website.dealSheet.label')}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: '0.5625rem',
                  color: ink,
                  fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                  fontWeight: 700,
                  mt: 0.25,
                  lineHeight: 1,
                }}
              >
                {dealNumber}
              </Typography>
            </Box>
          </Box>

          {/* Borrower */}
          <Box
            sx={{
              py: 1.25,
              borderTop: `1px solid ${ink}`,
              borderBottom: `1px solid ${border}`,
              mb: 1.75,
            }}
          >
            <Typography
              component="div"
              sx={{
                fontSize: '0.5rem',
                color: textMute,
                fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                letterSpacing: '0.04em',
                mb: 0.5,
                fontWeight: 600,
                textTransform: 'uppercase',
                lineHeight: 1,
              }}
            >
              {t('website.dealSheet.borrower')}
            </Typography>
            <Typography
              component="div"
              sx={{
                fontSize: '0.84375rem',
                color: ink,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
              }}
            >
              {borrower.name}
            </Typography>
            {borrower.meta && (
              <Typography
                component="div"
                sx={{
                  fontSize: '0.5625rem',
                  color: textDim,
                  mt: 0.25,
                  fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                  lineHeight: 1,
                }}
              >
                {borrower.meta}
              </Typography>
            )}
          </Box>

          {/* Property */}
          <Box sx={{ mb: 2 }}>
            <Typography
              component="div"
              sx={{
                fontSize: '0.5rem',
                color: textMute,
                fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                letterSpacing: '0.04em',
                mb: 0.5,
                fontWeight: 600,
                textTransform: 'uppercase',
                lineHeight: 1,
              }}
            >
              {t('website.dealSheet.property')}
            </Typography>
            <Typography component="div" sx={{ fontSize: '0.71875rem', color: ink, fontWeight: 500, lineHeight: 1.3 }}>
              {property.address}
            </Typography>
            {property.meta && (
              <Typography
                component="div"
                sx={{
                  fontSize: '0.5625rem',
                  color: textDim,
                  mt: 0.125,
                  fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                  lineHeight: 1,
                }}
              >
                {property.meta}
              </Typography>
            )}
          </Box>

          {/* Loan terms grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, mb: 1.75 }}>
            {cells.map(([label, value, big], i) => {
              if (!value) return null;
              return (
                <Box
                  key={label}
                  sx={{
                    py: 1,
                    borderBottom: i < 4 ? `1px solid ${border}` : 'none',
                    borderRight: i % 2 === 0 ? `1px solid ${border}` : 'none',
                    pr: i % 2 === 0 ? 1.5 : 0,
                    pl: i % 2 === 1 ? 1.5 : 0,
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontSize: '0.5rem',
                      color: textMute,
                      fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                      letterSpacing: '0.04em',
                      mb: 0.375,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      lineHeight: 1,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: big ? '1.0625rem' : '0.75rem',
                      color: ink,
                      fontWeight: 600,
                      letterSpacing: '-0.02em',
                      lineHeight: 1,
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Sizing strip — sand-toned */}
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              backgroundColor: bgWarm,
              borderRadius: 0.5,
              mb: 1.75,
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
              fontSize: '0.59375rem',
              color: textDim,
            }}
          >
            <Box component="span">
              {t('website.dealSheet.ltc')}{' '}
              <Box component="strong" sx={{ color: ink }}>
                {summary.ltc}
              </Box>
            </Box>
            <Box component="span">
              {t('website.dealSheet.ltarv')}{' '}
              <Box component="strong" sx={{ color: ink }}>
                {summary.ltarv}
              </Box>
            </Box>
            <Box component="span">
              {t('website.dealSheet.fico')}{' '}
              <Box component="strong" sx={{ color: ink }}>
                {summary.fico}
              </Box>
            </Box>
          </Box>

          {/* Signature line + stamp */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1, pr: 1.75 }}>
              <Box sx={{ height: 22, borderBottom: `1px solid ${ink}`, position: 'relative' }}>
                <Typography
                  component="div"
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    fontStyle: 'italic',
                    fontSize: '0.875rem',
                    color: ink,
                    fontWeight: 500,
                  }}
                >
                  {signature}
                </Typography>
              </Box>
              <Typography
                component="div"
                sx={{
                  fontSize: '0.5rem',
                  color: textMute,
                  fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                  mt: 0.375,
                  letterSpacing: '0.04em',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                {t('website.dealSheet.authorizedUnderwritten')}
              </Typography>
            </Box>
            {stamp && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  border: `1.5px solid ${stampColor}`,
                  color: stampColor,
                  fontSize: '0.5625rem',
                  fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  transform: 'rotate(-3deg)',
                  borderRadius: 0.375,
                  backgroundColor: alpha(stampColor, 0.04),
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                {stampLabel}
              </Box>
            )}
          </Box>
        </Box>

        {/* Bottom footer */}
        <Box
          sx={{
            px: 3,
            py: 1,
            backgroundColor: bgWarm,
            borderTop: `1px solid ${border}`,
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Monaco, Consolas, monospace',
            fontSize: '0.5rem',
            color: textMute,
            letterSpacing: '0.04em',
          }}
        >
          <Box component="span">PAGE 1 / 4</Box>
          <Box component="span">
            {brand.wordmark} {brand.wordmarkSub}
          </Box>
          <Box component="span">{dealNumber}</Box>
        </Box>
      </Box>
    </Box>
  );
});

DealSheetThumbnail.displayName = 'DealSheetThumbnail';

export default DealSheetThumbnail;
